import React from "react";
import CircularProgress from "@material-ui/core/CircularProgress";
import TextField, { TextFieldProps } from "@material-ui/core/TextField";
import Autocomplete, {
  RenderInputParams,
  AutocompleteProps,
} from "@material-ui/lab/Autocomplete";
import { UseAutocompleteSingleProps } from "@material-ui/lab/useAutocomplete";

export type CustomAutocompleteProps<T> = Omit<
  AutocompleteProps<T> & UseAutocompleteSingleProps<T>,
  | "loading"
  | "multiple"
  | "onClose"
  | "onOpen"
  | "open"
  | "options"
  | "renderInput"
> & {
  options?: T[];
  TextFieldProps?: TextFieldProps;
};

function CustomAutocomplete<T>(props: CustomAutocompleteProps<T>) {
  const { options, TextFieldProps, ...other } = props;
  const [open, setOpen] = React.useState(false);
  const loading = React.useMemo(() => {
    return open && options === undefined;
  }, [open, options]);

  const handleOpen = React.useCallback((_event: React.ChangeEvent<{}>) => {
    setOpen(true);
  }, []);

  const handleClose = React.useCallback((_event: React.ChangeEvent<{}>) => {
    setOpen(false);
  }, []);

  const renderInput = React.useCallback(
    (params: RenderInputParams): React.ReactNode => {
      return (
        <TextField
          {...params}
          {...TextFieldProps}
          InputProps={{
            ...params.InputProps,
            ...TextFieldProps?.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={20} />}
                {TextFieldProps?.InputProps?.endAdornment ||
                  params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      );
    },
    [TextFieldProps, loading]
  );

  return (
    <Autocomplete<T>
      {...other}
      options={options || []}
      loading={loading}
      multiple={false}
      open={open}
      onClose={handleClose}
      onOpen={handleOpen}
      renderInput={renderInput}
    />
  );
}

export default CustomAutocomplete;
